// services/ServiceProviderReviewService.js - ReactJS (web)
import api from '../api/api';

class ServiceProviderReviewService {
  // ==================== PUBLIC ROUTES ====================

  /**
   * Get all reviews for a provider with pagination
   * GET /api/service-providers/:providerId/reviews
   */
  async getReviews(providerId, page = 1, limit = 10, sortBy = 'date', sortOrder = 'desc') {
    try {
      const response = await api.get(`/service-providers/${providerId}/reviews`, {
        params: { page, limit, sortBy, sortOrder }
      }); 
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get review statistics for a provider
   * GET /api/service-providers/:providerId/reviews/statistics
   */
  async getReviewStatistics(providerId) {
    try {
      const response = await api.get(`/service-providers/${providerId}/reviews/statistics`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get reviews by rating filter
   * GET /api/service-providers/:providerId/reviews/rating/:rating
   */
  async getReviewsByRating(providerId, rating, page = 1, limit = 10) {
    try {
      const response = await api.get(`/service-providers/${providerId}/reviews/rating/${rating}`, {
        params: { page, limit }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get review trends (monthly statistics)
   * GET /api/service-providers/:providerId/reviews/trends
   */
  async getReviewTrends(providerId, months = 6) {
    try {
      const response = await api.get(`/service-providers/${providerId}/reviews/trends`, {
        params: { months }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get provider rating summary for public display
   * GET /api/service-providers/:providerId/reviews/summary
   */
  async getRatingSummary(providerId) {
    try {
      const response = await api.get(`/service-providers/${providerId}/reviews/summary`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== PROTECTED ROUTES ====================

  /**
   * Add a review to a service provider
   * POST /api/service-providers/:providerId/reviews
   */
  async addReview(providerId, reviewData) {
    try {
      const response = await api.post(`/service-providers/${providerId}/reviews`, reviewData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Flag a review as inappropriate
   * POST /api/service-providers/:providerId/reviews/:reviewId/flag
   */
  async flagReview(providerId, reviewId, reason) {
    try {
      const response = await api.post(`/service-providers/${providerId}/reviews/${reviewId}/flag`, { reason });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== ADMIN ROUTES ====================

  /**
   * Update a review (Admin only)
   * PUT /api/service-providers/:providerId/reviews/:reviewId
   */
  async updateReview(providerId, reviewId, updateData) {
    try {
      const response = await api.put(`/service-providers/${providerId}/reviews/${reviewId}`, updateData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete a review (Admin only)
   * DELETE /api/service-providers/:providerId/reviews/:reviewId
   */
  async deleteReview(providerId, reviewId) {
    try {
      const response = await api.delete(`/service-providers/${providerId}/reviews/${reviewId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== HELPER METHODS ====================

  /**
   * Handle API errors
   */
  handleError(error) {
    let errorMessage = ' ';
    
    if (error.response?.data?.data?.error?.message) {
      errorMessage = error.response.data.data.error.message;
    } else if (error.response?.data?.error?.message) {
      errorMessage = error.response.data.error.message;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    return {
      success: false,
      error: errorMessage,
      statusCode: error.response?.status,
      data: error.response?.data
    };
  }

  /**
   * Validate review data
   */
  validateReviewData(reviewData) {
    const errors = [];

    if (!reviewData.customerName) {
      errors.push('Customer name is required');
    }

    if (!reviewData.rating) {
      errors.push('Rating is required');
    } else if (reviewData.rating < 1 || reviewData.rating > 5) {
      errors.push('Rating must be between 1 and 5');
    }

    if (reviewData.comment && reviewData.comment.length > 500) {
      errors.push('Comment cannot exceed 500 characters');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Format rating for display
   */
  formatRating(rating) {
    if (!rating) return { stars: 0, display: 'No ratings yet' };
    
    const stars = Math.round(rating);
    const fullStars = '★'.repeat(stars);
    const emptyStars = '☆'.repeat(5 - stars);
    
    return {
      stars,
      display: `${fullStars}${emptyStars}`,
      percentage: (rating / 5) * 100,
      rating: rating.toFixed(1)
    };
  }

  /**
   * Get rating distribution percentages
   */
  getRatingPercentages(distribution, total) {
    if (!distribution || total === 0) {
      return { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    }

    const percentages = {};
    for (const [rating, count] of Object.entries(distribution)) {
      percentages[rating] = parseFloat(((count / total) * 100).toFixed(1));
    }
    return percentages;
  }

  /**
   * Get review sentiment based on rating
   */
  getReviewSentiment(rating) {
    if (rating >= 4.5) return { label: 'Excellent', emoji: '🌟', color: '#4CAF50' };
    if (rating >= 4.0) return { label: 'Very Good', emoji: '⭐', color: '#8BC34A' };
    if (rating >= 3.0) return { label: 'Good', emoji: '👍', color: '#FFC107' };
    if (rating >= 2.0) return { label: 'Fair', emoji: '👎', color: '#FF9800' };
    return { label: 'Poor', emoji: '😞', color: '#F44336' };
  }

  /**
   * Format date for display
   */
  formatDate(date) {
    if (!date) return null;
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  /**
   * Get time ago string
   */
  getTimeAgo(date) {
    if (!date) return null;
    const now = new Date();
    const diff = now - new Date(date);
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    if (weeks < 4) return `${weeks}w ago`;
    if (months < 12) return `${months}mo ago`;
    return `${years}y ago`;
  }

  /**
   * Check if review has been flagged
   */
  isReviewFlagged(review) {
    return review?.flagged || false;
  }

  /**
   * Get review status color
   */
  getReviewStatusColor(rating) {
    if (rating >= 4.5) return '#4CAF50';
    if (rating >= 4.0) return '#8BC34A';
    if (rating >= 3.0) return '#FFC107';
    if (rating >= 2.0) return '#FF9800';
    return '#F44336';
  }

  /**
   * Get review distribution data for chart
   */
  getDistributionData(distribution) {
    if (!distribution) return [];
    
    return Object.entries(distribution).map(([rating, count]) => ({
      rating: parseInt(rating),
      count,
      label: `${rating} Star${rating > 1 ? 's' : ''}`
    })).sort((a, b) => a.rating - b.rating);
  }

  /**
   * Calculate average rating from reviews
   */
  calculateAverageRating(reviews) {
    if (!reviews || reviews.length === 0) return 0;
    const sum = reviews.reduce((acc, review) => acc + review.rating, 0);
    return parseFloat((sum / reviews.length).toFixed(1));
  }

  /**
   * Format review for display (web helper)
   */
  formatReview(review) {
    if (!review) return null;

    return {
      ...review,
      id: review._id,
      formattedDate: this.formatDate(review.createdAt),
      timeAgo: this.getTimeAgo(review.createdAt),
      sentiment: this.getReviewSentiment(review.rating),
      ratingDisplay: this.formatRating(review.rating),
      isFlagged: this.isReviewFlagged(review),
      statusColor: this.getReviewStatusColor(review.rating)
    };
  }

  /**
   * Format multiple reviews
   */
  formatReviews(reviews) {
    if (!reviews || !Array.isArray(reviews)) return [];
    return reviews.map(review => this.formatReview(review));
  }

  /**
   * Get star rating HTML (web helper)
   */
  getStarRatingHTML(rating, size = 20) {
    if (!rating) return '';
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - Math.ceil(rating);

    let stars = '';
    for (let i = 0; i < fullStars; i++) {
      stars += `<span style="color: #FFD700; font-size: ${size}px;">★</span>`;
    }
    if (hasHalfStar) {
      stars += `<span style="color: #FFD700; font-size: ${size}px;">★</span>`;
    }
    for (let i = 0; i < emptyStars; i++) {
      stars += `<span style="color: #E0E0E0; font-size: ${size}px;">★</span>`;
    }
    return stars;
  }
}

export default new ServiceProviderReviewService();